import { useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, ChevronDown, FileSpreadsheet, CalendarPlus } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Lote 69 — botão "Exportar" (planilha .xlsx / calendário .ics), que antes ficava no Painel
// e agora vive nas abas Agenda e Calendário (pedido do Robson, 08/10/2026). A lógica é a
// mesma do Lote 23: buscar o arquivo, tentar o menu nativo de compartilhar (celular) e,
// se não houver, baixar direto (Safari/desktop) sem mostrar erro.
export default function ExportMenu({ className = "" }) {
  const [exporting, setExporting] = useState(false);

  const downloadFile = (file, filename) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Arquivo exportado", { description: filename });
  };

  const exportFile = async (path, filename, mimeType) => {
    setExporting(true);
    let file;
    try {
      const r = await http.get(path, { responseType: "blob" });
      file = new File([r.data], filename, { type: mimeType });
    } catch (e) {
      toast.error("Erro ao exportar a agenda");
      setExporting(false);
      return;
    }
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Agenda IApauta" });
      } else {
        downloadFile(file, filename);
      }
    } catch (e) {
      if (e?.name !== "AbortError") downloadFile(file, filename);
    } finally {
      setExporting(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const exportXlsx = () =>
    exportFile(
      "/publications/export",
      `iapauta-${today}.xlsx`,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );
  const exportIcs = () =>
    exportFile("/publications/export.ics", `iapauta-${today}.ics`, "text/calendar");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="export-button"
          disabled={exporting}
          variant="outline"
          size="sm"
          className={`gap-2 border-stone-300 hover:bg-stone-100 ${className}`}
        >
          <Download className="w-4 h-4" strokeWidth={1.75} />
          {exporting ? "Exportando…" : "Exportar"}
          <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.75} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid="export-xlsx-item" onClick={exportXlsx} className="gap-2">
          <FileSpreadsheet className="w-4 h-4" strokeWidth={1.75} /> Planilha (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="export-ics-item" onClick={exportIcs} className="gap-2">
          <CalendarPlus className="w-4 h-4" strokeWidth={1.75} /> Calendário (.ics)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
